import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { get, isArray, uniq, compact } from 'lodash';
import { EventToolbox } from 'client/utils/event-toolbox';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import './content-table.scss';

export function ContentTableUI({
  renderMap,
  sectionHeadingTag: SectionHeadingTag,
  keyPrefix,
  navSections,
  sharedClassNames,
  tableClassName,
  headingClassName: propHeadingClassName,
  labelComponentClassName,
  tableRef,
  colNumber,
  noBorders,
  isSmall,
  allowDesktopOverflow,
  hideSimilar,
  navListCustomEvent,
}) {
  const displayMap = compact(
    renderMap.map(section => {
      let displayFeatures = section.features.filter(({ data }) => data.some(d => !!d));
      if (hideSimilar) {
        // Render rows where no hash provided or where there is at least 1 different hash
        displayFeatures = displayFeatures.filter(
          ({ data }) =>
            data.some(d => !!d && !d.hash) ||
            data.filter(d => d.hash).some(d => d.hash !== data.find(featureData => featureData.hash).hash)
        );
      }

      return displayFeatures?.length ? { ...section, features: displayFeatures } : null;
    })
  );
  const navListItems = compact([
    ...displayMap.map(({ heading }) => get(navSections, `[${heading}].hash`)),
    ...displayMap.map(({ id }) => id),
  ]).join();

  useEffect(
    debounce(() => {
      if (navListCustomEvent) {
        EventToolbox.fireCustomEvent(navListCustomEvent, { navListItems: navListItems.split(',') });
      }
    }, 50),
    [navListCustomEvent, navListItems]
  );

  return (
    <table
      ref={tableRef}
      className={classnames(
        'content-table h-100',
        { 'no-borders': noBorders, 'allow-overflow': allowDesktopOverflow },
        tableClassName
      )}
      style={{ '--col-number': colNumber }}
    >
      <colgroup>
        <col width={0} />
        <col span={colNumber} width={isSmall ? '48%' : '25%'} />
      </colgroup>
      {displayMap.map(
        (
          {
            id,
            heading,
            features,
            alternateFeaturesBgColor = true,
            headingClassName = propHeadingClassName ||
              classnames({
                'heading-6': isSmall,
                'heading-4': !isSmall,
              }),
            sectionClassName,
            isThead,
            ref,
          },
          sectionIndex
        ) => {
          const sectionKey = `${keyPrefix}-section-${sectionIndex}`;
          const firstFeatureData = get(features, '[0].data');

          return (
            <ScrollElement
              tag={isThead ? 'thead' : 'tbody'}
              className={classnames(sectionClassName)}
              key={sectionKey}
              innerRef={ref}
              id={id || get(navSections, `[${heading}].hash`, sectionKey)}
            >
              {!!(heading && firstFeatureData) && (
                <tr>
                  <th scope="row" className="visually-hidden p-0">
                    Section Title
                  </th>
                  {new Array(firstFeatureData.length).fill().map((v, index) => {
                    const key = `${heading}-feature-heading-${index}`;
                    const hasContent = features.some(feature => get(feature, `data[${index}]`));

                    return (
                      <td key={key} className="py-0 px-0_5 px-md-1 pt-1_5">
                        {hasContent && (
                          <SectionHeadingTag
                            className={classnames('main-feature-heading pb-0_25 pt-0_5 pt-md-0', headingClassName)}
                          >
                            {heading}
                          </SectionHeadingTag>
                        )}
                      </td>
                    );
                  })}
                </tr>
              )}
              {features.map(
                (
                  {
                    data,
                    blank,
                    label,
                    rowLabel,
                    rowClassName,
                    cellClassName,
                    srOnly,
                    excludeAlternateFeaturesBgColor,
                    isDisplayFlex,
                  },
                  featureIndex
                ) => {
                  const featureKey = `${keyPrefix}-feature-${featureIndex}`;
                  const rowLabelComponent = rowLabel || (isArray(label) ? uniq(label).join('/') : label);
                  const DEFAULT_SR_ONLY_LABEL = 'Vehicle';

                  return (
                    <tr
                      key={featureKey}
                      className={classnames(rowClassName, get(sharedClassNames, 'rowClassName'), {
                        small: isSmall,
                        'visually-hidden': srOnly,
                      })}
                    >
                      <th scope="row" className="visually-hidden p-0">
                        {rowLabelComponent || DEFAULT_SR_ONLY_LABEL}
                      </th>
                      {data.map((featureData, dataIndex) => {
                        const key = `${keyPrefix}-feature-data-${dataIndex}`;
                        const labelComponent = isArray(label) ? get(label, `[${dataIndex}]`) : label;
                        const CellTag = isThead ? 'th' : 'td';
                        const cellScope = isThead ? 'col' : undefined;
                        const applyAlternatingBgColor =
                          (featureData || get(blank, `[${dataIndex}]`)) &&
                          !excludeAlternateFeaturesBgColor &&
                          alternateFeaturesBgColor &&
                          featureIndex % 2 === 1;
                        const cellTagClassName = isArray(cellClassName) ? cellClassName[dataIndex] : cellClassName;

                        return (
                          <CellTag
                            key={key}
                            scope={cellScope}
                            className={classnames(cellTagClassName, get(sharedClassNames, 'cellClassName'), {
                              'bg-gray-lightest': applyAlternatingBgColor,
                              empty: !featureData,
                            })}
                            data-test={`col${dataIndex + 1}`}
                          >
                            {!!featureData && (
                              <Fragment>
                                {!!label && (
                                  <div className={classnames('fw-bold', labelComponentClassName)} aria-hidden>
                                    {labelComponent}
                                  </div>
                                )}
                                <div
                                  data-test={classnames('feature', heading, labelComponent)}
                                  className={classnames('h-100', {
                                    'd-flex': isDisplayFlex,
                                  })}
                                >
                                  {featureData}
                                </div>
                              </Fragment>
                            )}
                          </CellTag>
                        );
                      })}
                    </tr>
                  );
                }
              )}
            </ScrollElement>
          );
        }
      )}
    </table>
  );
}

ContentTableUI.propTypes = {
  keyPrefix: PropTypes.string.isRequired,
  renderMap: PropTypes.arrayOf(
    PropTypes.shape({
      isThead: PropTypes.bool,
      ref: PropTypes.shape(),
      alternateFeaturesBgColor: PropTypes.bool,
      heading: PropTypes.string,
      rowLabel: PropTypes.string,
      features: PropTypes.arrayOf(
        PropTypes.shape({
          cellClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
          excludeAlternateFeaturesBgColor: PropTypes.bool,
          srOnly: PropTypes.bool,
          data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.bool])),
          blank: PropTypes.arrayOf(PropTypes.bool),
          label: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.arrayOf(PropTypes.string),
            PropTypes.node,
            PropTypes.arrayOf(PropTypes.node),
          ]),
          isDisplayFlex: PropTypes.bool,
        })
      ),
    })
  ).isRequired,
  sectionHeadingTag: PropTypes.string,
  navSections: PropTypes.shape(),
  sharedClassNames: PropTypes.shape({
    rowClassName: PropTypes.string,
    cellClassName: PropTypes.string,
  }),
  tableClassName: PropTypes.string,
  headingClassName: PropTypes.string,
  labelComponentClassName: PropTypes.string,
  navListCustomEvent: PropTypes.string,
  tableRef: PropTypes.shape(),
  colNumber: PropTypes.number,
  noBorders: PropTypes.bool,
  isSmall: PropTypes.bool,
  allowDesktopOverflow: PropTypes.bool,
  hideSimilar: PropTypes.bool,
};

ContentTableUI.defaultProps = {
  sectionHeadingTag: 'h2',
  navSections: null,
  sharedClassNames: null,
  tableClassName: null,
  headingClassName: null,
  labelComponentClassName: null,
  navListCustomEvent: null,
  tableRef: null,
  colNumber: 4,
  noBorders: false,
  isSmall: false,
  allowDesktopOverflow: false,
  hideSimilar: false,
};

export const ContentTable = React.memo(ContentTableUI);
